import { useMemo } from 'react';
import { UseQueryCustomResult } from '../types';
import { useLatestRepoTags } from './useDeploymentGroups';
import { useStaticContentLatestVersion } from './useStaticContent';
import {
  customComponentMap,
  DEPLOYMENT_GROUP_PRIMARYWEBAPPS,
} from '../components/VersionChecker/types';

/**
 * Maps a release name to its target Azure environment. Convention-based:
 *   - `*-prod` → prod
 *   - `*-uat` / `*-rc<n>` → uat
 *   - `*-preprod` / `*-qa` / `*-dev` → matching env
 *   - anything else → prod (sensible default for release pages).
 */
const envFromRelease = (release: string): string => {
  const lower = release.toLowerCase();
  if (/-rc\d*$/.test(lower)) return 'uat';
  const last = lower.split('-').pop() ?? '';
  if (['prod', 'preprod', 'uat', 'qa', 'dev'].includes(last)) return last;
  return 'prod';
};

/**
 * Returns the **latest** `GitTagOrBranch` value tagged on each
 * `(repo, region)` Azure resource in the release's target environment.
 *
 * Azure tags only track the latest deployment (not history), so for any
 * release that isn't the current state of a region, the returned tag will
 * be whatever's actually deployed there now — that's what the caller uses
 * to render "Has <release> tag but deployed as <actual> release."
 *
 * Components that don't deploy as Azure resources (BDirect MFE, published
 * to blob storage as static content) wouldn't otherwise appear in the
 * Azure-tag lookup. We overlay their deployment tags from the
 * StaticContentVersionLatest table — same workaround VersionChecker uses
 * for the MFE row.
 */
export const useReleaseDeployments = (
  release: string | null,
): UseQueryCustomResult<Record<string, Record<string, string>>> => {
  const env = release ? envFromRelease(release) : undefined;
  const azure = useLatestRepoTags(env);
  const staticMfe = useStaticContentLatestVersion(
    'bdirect-mfe',
    'js',
    env ?? '',
    DEPLOYMENT_GROUP_PRIMARYWEBAPPS,
  );

  const data = useMemo(() => {
    if (!azure.data && !staticMfe.data) return undefined;
    const result: Record<string, Record<string, string>> = {};
    for (const [repo, perRegion] of Object.entries(azure.data ?? {})) {
      result[repo] = { ...perRegion };
    }

    // Overlay static-content versions for repos listed in customComponentMap.
    // Inner keys come back as `<region>-<env>` (e.g. `aue-prod`); strip the
    // env to match the `Record<repo, Record<region, tag>>` shape.
    const staticRepoMap =
      customComponentMap[DEPLOYMENT_GROUP_PRIMARYWEBAPPS] ?? {};
    for (const [staticKey, perRegionEnv] of Object.entries(
      staticMfe.data ?? {},
    )) {
      const repo = staticRepoMap[staticKey]?.toLowerCase();
      if (!repo) continue;
      if (!result[repo]) result[repo] = {};
      for (const [regionEnv, version] of Object.entries(perRegionEnv)) {
        const tag = version?.tag;
        if (!tag) continue;
        const region = regionEnv.split('-')[0]?.toLowerCase();
        if (!region) continue;
        result[repo][region] = tag;
      }
    }

    return result;
  }, [azure.data, staticMfe.data]);

  return {
    data,
    isFetching: azure.isFetching || staticMfe.isFetching,
    error: azure.error ?? (staticMfe.error as Error | null) ?? null,
  };
};
